import { en, type Messages } from './locales/en';
import { zhTW } from './locales/zh-TW';
import { es } from './locales/es';
import { ca } from './locales/ca';
import { storage } from '../core/storage';

export type { Messages };
export type Lang = 'zh-TW' | 'en' | 'es' | 'ca';

export interface LangInfo {
  code: Lang;
  /** Native name, shown in the selector. */
  label: string;
  /** BCP-47 tag for speech synthesis. */
  speech: string;
}

export const LANGS: readonly LangInfo[] = [
  { code: 'zh-TW', label: '繁體中文', speech: 'zh-TW' },
  { code: 'en', label: 'English', speech: 'en-US' },
  { code: 'es', label: 'Español', speech: 'es-ES' },
  { code: 'ca', label: 'Català', speech: 'ca-ES' },
];

const DICTS: Record<Lang, Messages> = { 'zh-TW': zhTW, en, es, ca };
const KEY = 'lang';

let current: Lang = detectLang();
const listeners = new Set<() => void>();

export function detectLang(): Lang {
  const saved = storage.get(KEY);
  if (saved && saved in DICTS) return saved as Lang;
  const nav = typeof navigator !== 'undefined' ? navigator.languages ?? [navigator.language] : [];
  for (const raw of nav) {
    const l = raw.toLowerCase();
    if (l.startsWith('zh')) return 'zh-TW';
    if (l.startsWith('ca')) return 'ca';
    if (l.startsWith('es')) return 'es';
    if (l.startsWith('en')) return 'en';
  }
  return 'zh-TW';
}

/** The active message dictionary. Call it at render time, never cache the result. */
export function m(): Messages {
  return DICTS[current];
}

export function dictFor(lang: Lang): Messages {
  return DICTS[lang];
}

export function getLang(): Lang {
  return current;
}

export function langInfo(lang: Lang = current): LangInfo {
  return LANGS.find((l) => l.code === lang)!;
}

export function setLang(lang: Lang): void {
  if (lang === current) return;
  current = lang;
  storage.set(KEY, lang);
  document.documentElement.lang = lang;
  document.title = DICTS[lang].meta.title;
  listeners.forEach((fn) => fn());
}

export function onLangChange(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** Two-digit clock string. */
export function clock(totalMinutes: number): string {
  const h = Math.floor(totalMinutes / 60) % 24;
  const mm = Math.floor(totalMinutes % 60);
  return `${String(h).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
}
